import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalysisApiClientAccess, AppRoleService, checkIdentityRole } from '@nk/access-block';
import { CachingService } from '@nk/caching-lib';
import {
  AppFeatureKey,
  AppRoleDims,
  AppRoleKey,
  CasePartial,
  CompanyDetailsEntity,
  CompanyLocationEntity,
  COUNTRY_FEATURES,
  EntityMatchDecisionTypeEntity,
  ScreeningCaseCompositeEntity,
  ScreeningCaseReportEntity,
  ScreeningCaseReviewEntity,
  ScreeningCaseReviewStatusEntity,
  ScreeningEntityScreeningCaseCompositeEntity,
  ScreeningListComposite,
  SimpleCollection,
  WithUuid,
} from '@nk/data-entities-lib';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ScreeningCaseService {
  constructor(
    private _analysisApiClientAccess: AnalysisApiClientAccess,
    private _cachingService: CachingService,
    private _appRoleService: AppRoleService,
  ) {}

  public checkIdentityRole(): Observable<AppRoleDims> {
    return this._appRoleService.activeAppRoleDimsCache.get().pipe(checkIdentityRole([AppRoleKey.aml]));
  }

  public assignCase(
    screeningConfigUuid: string,
    caseUuid: string,
    payload: CasePartial,
  ): Observable<ScreeningCaseCompositeEntity> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiClientAccess.patch<ScreeningCaseCompositeEntity>(
          `/screening-config/${screeningConfigUuid}/screening-case/${caseUuid}`,
          payload,
          undefined,
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public screeningListCompositeCache = this._cachingService.create(this, this.screeningListComposite);
  public screeningListComposite(
    screeningConfigUuid: string,
    screeningCaseUuid: string,
  ): Observable<ScreeningListComposite[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('screening_case_uuid', screeningCaseUuid);

    return this._analysisApiClientAccess.get(
      `/screening-config/${screeningConfigUuid}/screening-list-composite`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.screening],
      [AppRoleKey.aml],
    );
  }

  public screeningCaseCompositeCache = this._cachingService.create(this, this.fetchScreeningCaseComposite);
  public fetchScreeningCaseComposite(
    screeningConfigUuid: string,
    screeningCaseUuid: string,
  ): Observable<ScreeningCaseCompositeEntity | null> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiClientAccess
          .get<
            ScreeningCaseCompositeEntity[]
          >(`/screening-config/${screeningConfigUuid}/screening-case-composite/${screeningCaseUuid}`, undefined, [AppFeatureKey.base, AppFeatureKey.screening], [AppRoleKey.aml])
          .pipe(map((data) => (data?.length ? data[0] : null)));
      }),
    );
  }

  public screeningEntityCaseCompositeCollectionCache = this._cachingService.create(
    this,
    this.fetchScreeningEntityCaseCompositeCollection,
  );
  public fetchScreeningEntityCaseCompositeCollection(
    screeningConfigUuid: string,
    screeningEntityUuid: string,
    limit: number,
    offset: number,
  ): Observable<SimpleCollection<ScreeningCaseCompositeEntity>> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        let httpParams = new HttpParams();
        httpParams = httpParams.append('limit', limit.toString());
        httpParams = httpParams.append('offset', offset.toString());
        return this._analysisApiClientAccess.get<SimpleCollection<ScreeningCaseCompositeEntity>>(
          `/screening-config/${screeningConfigUuid}/screening-entity/${screeningEntityUuid}/screening-case-composite-collection`,
          { params: httpParams },
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public screeningEntityScreeningCaseCompositeCollectionCache = this._cachingService.create(
    this,
    this.fetchScreeningEntityScreeningCaseCompositeCollection,
  );
  public fetchScreeningEntityScreeningCaseCompositeCollection(
    screeningConfigUuid: string,
    monitoringOriginEntityUuid: string | undefined,
    screeningEntityUuid: string | undefined,
    limit: number,
    offset: number,
  ): Observable<SimpleCollection<ScreeningEntityScreeningCaseCompositeEntity>> {
    let params = new HttpParams();
    if (monitoringOriginEntityUuid) {
      params = params.append('monitoring_origin_entity_uuid', monitoringOriginEntityUuid);
    }
    if (screeningEntityUuid) {
      params = params.append('screening_entity_uuid', screeningEntityUuid);
    }
    params = params.append('limit', limit);
    params = params.append('offset', offset);
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiClientAccess.get<SimpleCollection<ScreeningEntityScreeningCaseCompositeEntity>>(
          `/screening-config/${screeningConfigUuid}/screening-entity-screening-case-composite-collection`,
          { params },
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public screeningCaseReportCache = this._cachingService.create(this, this.fetchScreeningCaseReport);
  public fetchScreeningCaseReport(
    screeningConfigUuid: string,
    screeningCaseUuid: string,
  ): Observable<ScreeningCaseReportEntity | null> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        let httpParams = new HttpParams();
        httpParams = httpParams.append('screening_case_uuid', screeningCaseUuid);
        return this._analysisApiClientAccess
          .get<
            ScreeningCaseReportEntity[]
          >(`/screening-config/${screeningConfigUuid}/screening-case-report`, { params: httpParams }, [AppFeatureKey.base, AppFeatureKey.screening], [AppRoleKey.aml])
          .pipe(map((data) => (data?.length ? data[0] : null)));
      }),
    );
  }

  public entityMatchDecisionTypeListCache = this._cachingService.create(this, this.entityMatchDecisionTypeList);
  public entityMatchDecisionTypeList(): Observable<EntityMatchDecisionTypeEntity[]> {
    return this._analysisApiClientAccess.get<EntityMatchDecisionTypeEntity[]>(`/entity-match-decision-type`, {}, [
      AppFeatureKey.base,
      AppFeatureKey.screening,
    ]);
  }

  public screeningCaseReviewStatusListCache = this._cachingService.create(this, this.screeningCaseReviewStatusList);
  public screeningCaseReviewStatusList(): Observable<ScreeningCaseReviewStatusEntity[]> {
    return this._analysisApiClientAccess.get<ScreeningCaseReviewStatusEntity[]>(`/screening-case-review-status`, {}, [
      AppFeatureKey.base,
      AppFeatureKey.screening,
    ]);
  }

  public postScreeningCaseReviewEntity(
    screeningConfigUuid: string,
    payload: Partial<ScreeningCaseReviewEntity>,
  ): Observable<ScreeningCaseReviewEntity> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiClientAccess.post<ScreeningCaseReviewEntity>(
          `/screening-config/${screeningConfigUuid}/screening-case-review`,
          payload,
          undefined,
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public patchScreeningCaseReviewEntity(
    screeningConfigUuid: string,
    screeningCaseReviewUuid: string,
    payload: WithUuid<ScreeningCaseReviewEntity>,
  ): Observable<WithUuid<ScreeningCaseReviewEntity>> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiClientAccess.patch<WithUuid<ScreeningCaseReviewEntity>>(
          `/screening-config/${screeningConfigUuid}/screening-case-review/${screeningCaseReviewUuid}`,
          payload,
          undefined,
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public companyDetailsCache = this._cachingService.create(this, this.fetchCompanyDetails);
  public fetchCompanyDetails(companyUuid: string): Observable<CompanyDetailsEntity[]> {
    return this._analysisApiClientAccess.get<CompanyDetailsEntity[]>(`/company/${companyUuid}/company-details`, {}, [
      AppFeatureKey.base,
      ...COUNTRY_FEATURES,
    ]);
  }

  public companyLocationCache = this._cachingService.create(this, this.fetchCompanyLocation);
  public fetchCompanyLocation(companyUuid: string): Observable<CompanyLocationEntity[]> {
    return this._analysisApiClientAccess.get<CompanyLocationEntity[]>(`/company/${companyUuid}/company-location`, {}, [
      AppFeatureKey.base,
      ...COUNTRY_FEATURES,
    ]);
  }
}
